import React from "react";
import {
  Edit,
  Trash2,
  ChevronUp,
  ChevronDown,
  Download,
  Plus,
} from "lucide-react";
import { Category } from "../types/categories";

// Temporary static data
const staticCategories: Category[] = [
  {
    id: "1",
    name: "Hair Care",
    description: "Shampoos, conditioners, and treatments",
    status: "active",
    image: "https://via.placeholder.com/48x48.png?text=HC",
  },
  {
    id: "2",
    name: "Skin Care",
    description: "Lotions and moisturizers",
    status: "inactive",
    image: "https://via.placeholder.com/48x48.png?text=SC",
  },
  {
    id: "3",
    name: "Makeup",
    description: "Foundations, lipsticks, and more",
    status: "active",
    image: "https://via.placeholder.com/48x48.png?text=MU",
  },
  {
    id: "4",
    name: "Fragrance",
    description: "Perfumes and body sprays",
    status: "inactive",
    image: "https://via.placeholder.com/48x48.png?text=FR",
  },
  {
    id: "5",
    name: "Nail Care",
    description: "Nail polish and tools",
    status: "active",
    image: "https://via.placeholder.com/48x48.png?text=NC",
  },
  {
    id: "6",
    name: "Tools",
    description: "Brushes and accessories",
    status: "inactive",
    image: "https://via.placeholder.com/48x48.png?text=TL",
  },
  {
    id: "7",
    name: "Men’s Grooming",
    description: "Shaving creams and razors",
    status: "active",
    image: "https://via.placeholder.com/48x48.png?text=MG",
  },
  {
    id: "8",
    name: "Spa Products",
    description: "Relaxation essentials",
    status: "active",
    image: "https://via.placeholder.com/48x48.png?text=SP",
  },
];

// ✅ Props interface
interface CategoriesTableProps {
  onEdit?: (cat: Category) => void;
  onDelete?: (cat: Category) => void;
  onCreateNew?: () => void; // 🔹 NEW
}

const CategoriesTable: React.FC<CategoriesTableProps> = ({
  onEdit,
  onDelete,
  onCreateNew,
}) => {
  return (
    <div className="w-full bg-transparent lg:bg-white p-0 lg:p-6 rounded-xl lg:shadow">
      {/* Top Controls */}
      <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between mb-6 gap-4">
        <div className="flex items-center gap-2 text-sm text-gray-600 px-4 lg:px-0">
          <span>Showing</span>
          <span className="bg-[#F9F9F9] px-2.5 py-1 rounded-lg text-[#402B69] font-bold border border-[#BBB9F3] shadow-sm">
            {staticCategories.length}
          </span>
          <span className="font-medium">categories</span>
        </div>

        <div className="flex flex-wrap items-center gap-2 px-4 lg:px-0">
          <button
            onClick={onCreateNew}
            className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2.5 bg-[#885ABB] hover:bg-[#6f47a2] text-white rounded-xl text-sm font-bold shadow-sm transition-all"
          >
            <Plus className="w-4 h-4" />
            <span>Create New</span>
          </button>
          <button className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-xl text-sm font-bold shadow-sm transition-all">
            <Trash2 className="w-4 h-4" />
            <span className="hidden sm:inline">Delete All</span>
            <span className="sm:hidden">Delete</span>
          </button>
          <button className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2.5 bg-[#885ABB] hover:bg-[#6f47a2] text-white rounded-xl text-sm font-bold shadow-sm transition-all">
            <Download className="w-4 h-4" />
            <span>Export</span>
          </button>
        </div>

        <div className="relative px-4 lg:px-0">
          <input
            type="text"
            placeholder="Search categories..."
            className="px-4 py-2.5 border border-[#BBB9F3] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#885ABB]/20 focus:border-[#885ABB] text-sm w-full lg:w-64 bg-white shadow-sm transition-all"
          />
        </div>
      </div>

      {/* Table Container */}
      <div className="bg-transparent lg:bg-white lg:rounded-xl overflow-hidden lg:border lg:border-[#BBB9F3]">
        <div className="overflow-x-auto">
          <table className="w-full border-separate border-spacing-y-4 lg:border-collapse lg:border-spacing-0 px-4 lg:px-0">
            <thead className="hidden lg:table-header-group">
              <tr className="bg-[#F9F9F9] border-b border-[#BBB9F3]">
                {["#", "Image", "Name", "Description", "Status", "Action"].map(
                  (header) => (
                    <th
                      key={header}
                      className="px-6 py-4 text-left text-sm font-bold text-[#402B69]"
                    >
                      <div className="flex items-center gap-2">
                        {header}
                        <div className="flex flex-col">
                          <ChevronUp className="w-3 h-3 text-gray-400" />
                          <ChevronDown className="w-3 h-3 text-gray-400 -mt-1" />
                        </div>
                      </div>
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody className="flex flex-col gap-4 lg:table-row-group">
              {staticCategories.map((cat, index) => (
                <tr
                  key={cat.id}
                  className={`flex flex-col lg:table-row border border-[#BBB9F3] lg:border-0 lg:border-b lg:border-[#EEE] hover:bg-[#F9F9F9] transition-colors rounded-xl lg:rounded-none bg-white lg:bg-transparent p-5 lg:p-0 shadow-sm lg:shadow-none relative group`}
                >
                  <td className="lg:px-6 lg:py-4 text-sm text-gray-700">
                    <span className="lg:hidden text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Row</span>
                    <span className="font-bold lg:font-normal">{index + 1}</span>
                  </td>
                  <td className="lg:px-6 lg:py-4 mt-2 lg:mt-0">
                    <div className="w-16 h-16 lg:w-12 lg:h-12 bg-gray-100 rounded-xl border border-[#BBB9F3] flex items-center justify-center overflow-hidden shadow-sm">
                      {cat.image ? (
                        <img
                          src={cat.image}
                          alt={cat.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="text-[10px] text-gray-400 text-center px-1 font-bold">
                          NO IMAGE
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="lg:px-6 lg:py-4 mt-3 lg:mt-0">
                    <span className="lg:hidden text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Name</span>
                    <span className="text-base lg:text-sm font-bold lg:font-semibold text-gray-900 lg:text-gray-700">
                      {cat.name}
                    </span>
                  </td>
                  <td className="lg:px-6 lg:py-4 mt-2 lg:mt-0 lg:max-w-xs">
                    <span className="lg:hidden text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Description</span>
                    <span className="text-sm text-gray-500 line-clamp-2 lg:line-clamp-none">
                      {cat.description || "—"}
                    </span>
                  </td>
                  <td className="lg:px-6 lg:py-4 mt-2 lg:mt-0">
                    <span className="lg:hidden text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Status</span>
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border ${cat.status === "active"
                          ? "bg-green-100 text-green-700 border-green-200"
                          : "bg-gray-100 text-gray-600 border-gray-200"
                        }`}
                    >
                      {cat.status.toUpperCase()}
                    </span>
                  </td>
                  <td className="lg:px-6 lg:py-4 mt-5 lg:mt-0 border-t lg:border-0 border-gray-100 pt-4 lg:pt-0">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => onEdit?.(cat)}
                        className="flex-1 lg:flex-none flex items-center justify-center gap-2 px-4 py-2.5 lg:p-2 bg-[#F3F0FF] lg:bg-transparent text-[#402B69] hover:bg-[#885ABB] hover:text-white rounded-xl lg:rounded-lg transition-all font-bold lg:font-normal"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                        <span className="lg:hidden">Edit</span>
                      </button>
                      <button
                        onClick={() => onDelete?.(cat)}
                        className="flex-1 lg:flex-none flex items-center justify-center gap-2 px-4 py-2.5 lg:p-2 bg-red-50 lg:bg-transparent text-red-600 hover:bg-red-600 hover:text-white rounded-xl lg:rounded-lg transition-all font-bold lg:font-normal"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span className="lg:hidden">Delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CategoriesTable;
